import { FC } from "react";
import { hexToString, toBytes } from "viem";

import {
  useAllowance,
  useMetamask,
  useServerSignature,
  useWriteContractWithSimulation,
} from "@dzambalaorg/react-hooks-eth";
import { type IServerSignature, TokenType } from "@dzambalaorg/types-blockchain";
import {
  convertDatabaseAssetToChainAsset,
  convertDatabaseAssetToTokenTypeAsset,
  getEthPrice,
} from "@framework/exchange";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, IContract, ILootBox } from "@framework/types";
import ExchangeLootBoxFacetPurchaseLootABI from "@framework/abis/json/ExchangeLootBoxFacet/purchaseLoot.json";
import { ZeroAddress } from "@framework/constants";

interface ILootBoxBuyButtonProps {
  className?: string;
  disabled?: boolean;
  lootBox: ILootBox;
  variant?: ListActionVariant;
}

export const LootBoxPurchaseButton: FC<ILootBoxBuyButtonProps> = props => {
  const { className, disabled, lootBox, variant = ListActionVariant.button } = props;

  const { writeContractAsync } = useWriteContractWithSimulation();

  const referrer = ZeroAddress;

  const { execute: metaFnWithAllowance } = useAllowance(async (sign: IServerSignature, systemContract: IContract) => {
    const content = convertDatabaseAssetToChainAsset([...lootBox.content!.components]);
    const price = convertDatabaseAssetToChainAsset([...lootBox.template!.price!.components]);

    return writeContractAsync({
      address: systemContract.address as AddressType,
      abi: ExchangeLootBoxFacetPurchaseLootABI,
      functionName: "purchaseLoot",
      args: [
        {
          externalId: lootBox.id,
          expiresAt: sign.expiresAt,
          nonce: toBytes(sign.nonce, { size: 32 }),
          extra: hexToString("0x", { size: 32 }),
          receiver: lootBox.template!.contract!.merchant!.wallet,
          referrer: ZeroAddress,
        },
        {
          tokenType: Object.values(TokenType).indexOf(TokenType.ERC721),
          token: lootBox.template!.contract!.address,
          tokenId: lootBox.templateId,
          amount: "1",
        },
        price,
        content,
        {
          min: lootBox.min,
          max: lootBox.max,
        },
        sign.signature,
        {
          value: getEthPrice(lootBox.template?.price),
        },
      ],
    });
  });

  const metaFnWithSign = useServerSignature((_values: null, sign: IServerSignature, systemContract: IContract) => {
    const price = convertDatabaseAssetToTokenTypeAsset(lootBox.template!.price!.components);
    return metaFnWithAllowance({ contract: systemContract.address, assets: price }, sign, systemContract);
  });

  const { execute: metaFn } = useMetamask(() => {
    return metaFnWithSign(
      {
        url: "/loot/sign",
        method: "POST",
        data: {
          referrer,
          lootBoxId: lootBox.id,
        },
      },
      null,
    ) as Promise<void>;
  });

  const handleBuy = async () => {
    await metaFn();
  };

  return (
    <ListAction
      onClick={handleBuy}
      message="form.buttons.buy"
      className={className}
      dataTestId="LootBoxPurchaseButton"
      disabled={disabled}
      variant={variant}
    />
  );
};
